(function () {
  const swiperInstances = new Map();
  const sectionClones = new Map();
  const buttonWrapperPaddingTop = 1.2;
  const safeOffset = 0.2;
  const initializedVideos = new WeakSet();
  const lazyVideoObservers = new WeakMap();
  const autoplayVideoObservers = new WeakMap();
  const calcButtonWrapperHeight = (slide) => {
    const buttonWrapper = slide.querySelector(
      ".social-feed-slide-product-button-wrapper"
    );
    if (!buttonWrapper) return;
    const buttonWrapperHeightInRem = buttonWrapper.scrollHeight / 10;
    slide.style.setProperty(
      "--button-wrapper-height",
      `${buttonWrapperHeightInRem + buttonWrapperPaddingTop + safeOffset}rem`
    );
  };
  const initVideoButtons = (slide) => {
    const video = slide.querySelector(".social-feed-slide-video");
    const soundButton = slide.querySelector(".js-sound-video");
    const playButton = slide.querySelector(".js-play-video");
    const playButtonWrapper = slide.querySelector(
      ".social-feed-slide-play-wrapper"
    );
    if (video) {
      video.onended = () => {
        playButtonWrapper?.classList.add("video-paused");
      };
      if (soundButton) {
        soundButton.addEventListener("click", () => {
          if (video.muted) {
            video.muted = false;
            soundButton.classList.remove("muted");
          } else {
            video.muted = true;
            soundButton.classList.add("muted");
          }
        });
      }
      if (playButton && playButtonWrapper) {
        playButton.addEventListener("click", () => {
          if (video.paused) {
            video.play();
            playButtonWrapper.classList.remove("video-paused");
          } else {
            video.pause();
            playButtonWrapper.classList.add("video-paused");
          }
        });
      }
    }
  };
  const initLazyVideo = (slide) => {
    const video = slide.querySelector(".social-feed-slide-video");
    if (!video || initializedVideos.has(video)) return;

    initializedVideos.add(video);

    const hydrate = () => {
      if (video.dataset.poster) {
        video.poster = video.dataset.poster;
        video.removeAttribute("data-poster");
      }
      if (video.preload === "none") {
        video.preload = "metadata";
        video.load();
      }
      lazyVideoObservers.get(video)?.disconnect();
      lazyVideoObservers.delete(video);
    };

    video.addEventListener("play", hydrate, { once: true });

    if ("IntersectionObserver" in window) {
      const lazyObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) hydrate();
        },
        { rootMargin: "600px 0px", threshold: 0 }
      );
      lazyObserver.observe(video);
      lazyVideoObservers.set(video, lazyObserver);
    } else {
      hydrate();
    }

    if (!video.classList.contains("autoplay-persistent")) return;

    video.autoplay = false;
    video.removeAttribute("autoplay");
    video.pause();

    if (!("IntersectionObserver" in window)) {
      hydrate();
      video.play().catch(() => {});
      return;
    }

    const controls = slide.querySelector(".social-feed-slide-play-wrapper");
    const autoplayObserver = new IntersectionObserver(
      ([entry]) => {
        const shouldPlay =
          entry.isIntersecting && entry.intersectionRatio >= 0.6;

        controls?.classList.toggle("video-paused", !shouldPlay);

        if (shouldPlay) {
          hydrate();
          video
            .play()
            .catch(() => controls?.classList.add("video-paused"));
        } else {
          video.pause();
        }
      },
      { threshold: [0, 0.6] }
    );

    autoplayObserver.observe(video);
    autoplayVideoObservers.set(video, autoplayObserver);
  };
  const disconnectVideoObservers = (slide) => {
    const video = slide.querySelector(".social-feed-slide-video");
    if (!video) return;
    lazyVideoObservers.get(video)?.disconnect();
    autoplayVideoObservers.get(video)?.disconnect();
    lazyVideoObservers.delete(video);
    autoplayVideoObservers.delete(video);
  };
  const completelyClearSection = (sectionId) => {
    const existingInstance = swiperInstances.get(sectionId);
    if (existingInstance) {
      try {
        existingInstance.destroy(true, true);
      } catch (e) {
        console.warn("Error destroying swiper:", e);
      }
      swiperInstances.delete(sectionId);
    }
    sectionClones.delete(sectionId);
  };
  const getSectionId = (section) => {
    return (
      section?.dataset?.sectionId ||
      section?.id ||
      section?.getAttribute("id") ||
      section?.querySelector("[data-section-id]")?.dataset?.sectionId
    );
  };
  const initSocialFeedSlider = (section) => {
    if (!section || !section?.classList.contains("social-feed-section")) return;
    const sectionId = getSectionId(section);
    if (!sectionId) {
      console.warn("Section ID not found");
      return;
    }
    completelyClearSection(sectionId);
    setTimeout(() => {
      const swiperEl = section.querySelector(".social-feed-slider");
      if (!swiperEl) return;
      const wrapper = swiperEl.querySelector(".swiper-wrapper");
      if (wrapper) {
        const newWrapper = wrapper.cloneNode(true);
        wrapper.parentNode.replaceChild(newWrapper, wrapper);
      }
      const prevBtn = section.querySelector(".social-feed-nav-button--prev");
      const nextBtn = section.querySelector(".social-feed-nav-button--next");
      const columns = Number(swiperEl.dataset.columns) || 3;
      const isEqualsPerView = Boolean(swiperEl.dataset.equalsPerView);
      const isCenteredSlides = Boolean(swiperEl.dataset.centeredSlides);
      const isLoop = Boolean(swiperEl.dataset.loop);
      const isAutoplay = Boolean(swiperEl.dataset.autoplay);
      const sliderSpeed = Number(swiperEl.dataset.speed) || 1200;
      const sliderAutoplaySpeed =
        Number(swiperEl.dataset.autoplaySpeed) || 8000;
      const breakpointsConfig = {
        3: {
          750: { slidesPerView: 2.1, spaceBetween: 20 },
          990: {
            slidesPerView: isEqualsPerView ? columns : 3.15,
            spaceBetween: 24,
          },
        },
        4: {
          750: { slidesPerView: 2.1, spaceBetween: 20 },
          990: { slidesPerView: 3.15, spaceBetween: 24 },
          1360: {
            slidesPerView: isEqualsPerView ? columns : 4.15,
            spaceBetween: 24,
          },
        },
        5: {
          750: { slidesPerView: 2.1, spaceBetween: 20 },
          990: { slidesPerView: 3.15, spaceBetween: 24 },
          1360: { slidesPerView: 4.15, spaceBetween: 24 },
          1536: {
            slidesPerView: isEqualsPerView ? columns : 5.15,
            spaceBetween: 24,
          },
        },
      };
      const centeredSlidesBreakpointsConfig = {
        3: {
          576: { slidesPerView: 1.5, spaceBetween: 20 },
          750: { slidesPerView: 1.75, spaceBetween: 20 },
          990: {
            slidesPerView: isEqualsPerView ? columns : 3.5,
            spaceBetween: 24,
          },
        },
        5: {
          576: { slidesPerView: 1.5, spaceBetween: 20 },
          750: { slidesPerView: 1.75, spaceBetween: 20 },
          990: { slidesPerView: 3.15, spaceBetween: 24 },
          1200: { slidesPerView: 3.5, spaceBetween: 24 },
          1536: {
            slidesPerView: isEqualsPerView ? columns : 5.15,
            spaceBetween: 24,
          },
        },
      };
      const sliderOptions = {
        slidesPerView: 1.15,
        spaceBetween: 16,
        speed: isAutoplay ? sliderAutoplaySpeed : sliderSpeed,
        allowTouchMove: true,
        loop: isLoop,
        centeredSlides: isCenteredSlides,
        centeredSlidesBounds: isCenteredSlides,
        observer: true,
        observeParents: true,
        observeSlideChildren: true,
        breakpoints: isCenteredSlides
          ? centeredSlidesBreakpointsConfig[columns]
          : breakpointsConfig[columns],
        mousewheel: {
          forceToAxis: true,
        },
        autoplay: isAutoplay
          ? {
              delay: 0,
              pauseOnMouseEnter: false,
              disableOnInteraction: false,
              reverseDirection: true,
              waitForTransition: true,
            }
          : false,
        preventClicks: true,
        on: {
          init: function (swiper) {
            swiperEl._swiperInstance = swiper;
            const slides = swiper.slides;
            slides.forEach((slide) => {
              calcButtonWrapperHeight(slide);
              initVideoButtons(slide);
              initLazyVideo(slide);
            });
            if (isAutoplay) {
              let savedTransform = null;
              let savedTargetTranslate = null;
              let pausedTranslate = null;
              let wasAutoplayRunning = false;
              let wasTouched = false;
              const getSlideWidth = () => swiper.slides[0]?.offsetWidth || 0;
              const getMovementSpeed = () =>
                getSlideWidth() / swiper.params.speed;
              const mouseEnterHandler = () => {
                if (wasTouched || !swiper.autoplay) return;
                wasAutoplayRunning = swiper.autoplay.running;
                if (wasAutoplayRunning) {
                  swiper.autoplay.stop();
                  savedTransform = swiper.wrapperEl.style.transform;
                  const targetMatch = savedTransform.match(
                    /translate3d\(([^,]+)px/
                  );
                  savedTargetTranslate = targetMatch
                    ? parseFloat(targetMatch[1])
                    : swiper.getTranslate();
                  pausedTranslate = swiper.getTranslate();
                  swiper.wrapperEl.style.transitionDuration = "0ms";
                  swiper.wrapperEl.style.transform = `translate3d(${pausedTranslate}px, 0px, 0px)`;
                  swiper.params.speed = sliderSpeed;
                  swiper.wrapperEl.style.transitionTimingFunction = "ease";
                }
              };
              const mouseLeaveHandler = () => {
                if (!wasAutoplayRunning || wasTouched || !swiper.autoplay)
                  return;
                swiper.params.speed = sliderAutoplaySpeed;
                swiper.wrapperEl.style.transitionTimingFunction = "linear";
                const remainingDistance = Math.abs(
                  savedTargetTranslate - pausedTranslate
                );
                const proportionalDuration =
                  remainingDistance / getMovementSpeed();
                swiper.wrapperEl.style.transitionDuration =
                  proportionalDuration + "ms";
                swiper.wrapperEl.style.transform = savedTransform;
                setTimeout(() => {
                  if (swiper.autoplay && !swiper.destroyed) {
                    swiper.autoplay.start();
                  }
                }, 50);
                wasAutoplayRunning = false;
              };
              const touchStartHandler = () => {
                wasTouched = true;
                wasAutoplayRunning = false;
              };
              const touchEndHandler = () => {
                setTimeout(() => {
                  wasTouched = false;
                }, 300);
              };
              swiper.el.addEventListener("mouseenter", mouseEnterHandler);
              swiper.el.addEventListener("mouseleave", mouseLeaveHandler);
              swiper.on("touchStart", touchStartHandler);
              swiper.on("touchEnd", touchEndHandler);
              swiper._customHandlers = {
                mouseEnterHandler,
                mouseLeaveHandler,
                touchStartHandler,
                touchEndHandler,
              };
            }
          },
          beforeDestroy: function (swiper) {
            swiper.slides.forEach(disconnectVideoObservers);
            if (swiper._customHandlers) {
              swiper.el.removeEventListener(
                "mouseenter",
                swiper._customHandlers.mouseEnterHandler
              );
              swiper.el.removeEventListener(
                "mouseleave",
                swiper._customHandlers.mouseLeaveHandler
              );
            }
            if (swiper.autoplay) {
              swiper.autoplay.stop();
            }
          },
        },
        navigation:
          !isAutoplay && nextBtn && prevBtn
            ? {
                nextEl: nextBtn,
                prevEl: prevBtn,
                disabledClass: "swiper-button-disabled",
              }
            : false,
      };
      const newInstance = new Swiper(swiperEl, sliderOptions);
      swiperInstances.set(sectionId, newInstance);
      if (isAutoplay && newInstance.autoplay) {
        setTimeout(() => {
          if (!newInstance.destroyed && newInstance.autoplay) {
            newInstance.autoplay.start();
          }
        }, 100);
      }
    }, 50);
  };
  const initHeadingAnimation = (section) => {
    if (!section || !section?.classList.contains("social-feed-section")) return;
    const enableAnimation = section.querySelector(
      "[data-enable-header-animation]"
    );
    if (!enableAnimation) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const section = entry.target;
            section.classList.add("anim-active");
            observer.unobserve(section);
          }
        });
      },
      {
        threshold: 0,
        rootMargin: "0px 0px -25% 0px",
      }
    );
    observer.observe(section);
  };
  const initialSection = document.currentScript?.parentElement;
  if (initialSection) {
    initSocialFeedSlider(initialSection);
    initHeadingAnimation(initialSection);
  }
  document.addEventListener("shopify:section:load", function (event) {
    setTimeout(() => {
      initSocialFeedSlider(event.target);
      initHeadingAnimation(event.target);
    }, 150);
  });
  document.addEventListener("shopify:section:unload", function (event) {
    const sectionId = getSectionId(event.target);
    if (sectionId) {
      completelyClearSection(sectionId);
    }
  });
})();
